import FileSaver from 'file-saver';
import { useState } from 'react';
import { toast } from 'react-hot-toast';

import { BasicMenu } from '@client/new/components/BasicMenu/BasicMenu';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { trackEvent } from '@services/tracking';

import {
  downloadExportAll,
  getLeadsUsers,
  getOtpUsers,
  getSubscribedUsers,
} from './CreatorCrm.api';
import { useCrmStore } from './CreatorCrm.store';
import { CrmSegment } from './CrmSegment.enum';

export const ExportButton = () => {
  const [loading, setLoading] = useState(false);
  const crmStore = useCrmStore();
  const influencerId = useInfluencerId();

  const Queries = {
    [CrmSegment.Subscribed]: async () => {
      const result = await getSubscribedUsers(influencerId, {
        pagination: undefined,
        exportCsv: true,
        classification: crmStore.getClassification(),
        status: crmStore.filters.subscription,
        product: crmStore.filters.product,
        searchTerm: crmStore.searchTerm,
        label: crmStore.filters.additional,
      });

      return result;
    },
    [CrmSegment.ExclusiveBuyers]: async () => {
      const result = await getOtpUsers(influencerId, {
        exportCsv: true,
        pagination: undefined,
        otpOffers: crmStore.filters.otpOffers,
        searchTerm: crmStore.searchTerm,
        status: crmStore.filters.subscription,
      });

      return result;
    },
    [CrmSegment.Leads]: async () => {
      const result = await getLeadsUsers(influencerId, {
        exportCsv: true,
        pagination: undefined,
        searchTerm: crmStore.searchTerm,
        source: crmStore.filters.leadsSource,
      });

      return result;
    },
  };

  const exportQuery = Queries[crmStore.segment];

  const handleSegmentExportClick = () => {
    if (loading) {
      return;
    }
    setLoading(true);

    trackEvent({
      type: 'Tapped Export This Table',
      payload: {},
    });

    const fileName = `crm-${
      crmStore.segment
    }-users-${new Date().toLocaleDateString()}`.toLocaleLowerCase();

    exportQuery()
      .then((data) => saveCsvFile(data, fileName))
      .then(() => toast.success(`Exported ${fileName}.csv file`))
      .catch((err) =>
        toast.error(
          `Could not export user list. Try again later. Error: ${err}`,
        ),
      )
      .finally(() => setLoading(false));
  };

  const handleExportClick = () => {
    if (loading) {
      return;
    }
    setLoading(true);

    trackEvent({
      type: 'Tapped Export All Data',
      payload: {},
    });

    const fileName =
      `crm-users-${new Date().toLocaleDateString()}`.toLocaleLowerCase();

    try {
      downloadExportAll(influencerId);
      setLoading(false);
      toast.success(`Started exporting the ${fileName}.csv file`);
    } catch (error) {
      setLoading(false);
      toast.error(
        `Could not export user list. Try again later. Error: ${error}`,
      );
    }
  };

  return (
    <div className="mt-5">
      <BasicMenu
        disabled={loading}
        title="Export"
        items={[
          { label: 'Export all data', onClick: handleExportClick },
          { label: 'Export this table', onClick: handleSegmentExportClick },
        ]}
      />
    </div>
  );
};

function saveCsvFile(data: string, fileName: string) {
  const blob = new Blob([data], {
    type: 'text/plain;charset=utf-8',
  });
  FileSaver.saveAs(blob, `${fileName}.csv`);
}
